import React, { useState, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  LinearProgress,
} from "@mui/material";
import { DATE_DISPLAY_FORMAT, toApiDate } from "../../utils/dateUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DataGrid } from "@mui/x-data-grid";
import {
  ArrowBack,
  Download,
  FilterList,
  Assignment,
} from "@mui/icons-material";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const AssignmentsAnalytics = () => {
  const navigate = useNavigate();
  const { isInInstituteContext, selectedInstitute } = useSelector(
    (state) => state.instituteContext
  );
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.role?.slug || user?.role || "student";
  const userInstitute = user?.institute?.name || "ABC Engineering College";
  const canViewGlobalData = userRole === "superadmin" && !isInInstituteContext;

  const [instituteFilter, setInstituteFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [institutes, setInstitutes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [summary, setSummary] = useState({ totalAssignments: 0, submitted: 0, pending: 0, rejected: 0, active: 0, completed: 0, overdue: 0 });
  const [period, setPeriod] = useState({ startDate: null, endDate: null });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const analyticsService = (await import("../../services/analyticsService")).default;
        const instituteService = (await import("../../services/instituteService")).default;
        const deptService = (await import("../../services/departmentService")).default;

        if (canViewGlobalData) {
          const instRes = await instituteService.getInstitutes({ page: 1, limit: 50 });
          if (mounted && instRes?.data?.data) setInstitutes(instRes.data.data);
        }
        const deptRes = await deptService.getAllDepartments({ page: 1, limit: 100 });
        if (mounted && deptRes?.data?.data) setDepartments(deptRes.data.data);

        const res = await analyticsService.getAssignments({
          ...(instituteFilter && canViewGlobalData ? { instituteId: instituteFilter } : {}),
          ...(departmentFilter ? { departmentId: departmentFilter } : {}),
          ...(fromDate && toDate ? { from: fromDate, to: toDate } : {}),
        });
        if (mounted) {
          const payload = res?.data?.data ?? res?.data ?? {};
          const rows = payload?.rows ?? payload ?? [];
          setAssignments(Array.isArray(rows) ? rows : []);
          setPeriod(payload?.period || { startDate: null, endDate: null });
          setSummary(payload?.summary || { totalAssignments: 0, submitted: 0, pending: 0, rejected: 0, active: 0, completed: 0, overdue: 0 });
        }
      } catch (e) {
        console.warn("Failed to load assignments analytics:", e?.message || e);
        if (mounted) {
          setAssignments([]);
          setPeriod({ startDate: null, endDate: null });
          setSummary({ totalAssignments: 0, submitted: 0, pending: 0, rejected: 0, active: 0, completed: 0, overdue: 0 });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, [canViewGlobalData, instituteFilter, departmentFilter, fromDate, toDate]);

  const statusData = {
    labels: ["Submitted", "Pending", "Overdue"],
    datasets: [
      {
        data: [summary.submitted || 0, summary.pending || 0, summary.overdue || 0],
        backgroundColor: ["#4caf50", "#ff9800", "#f44336"],
        borderWidth: 0,
      },
    ],
  };

  const weeklyData = {
    labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
    datasets: [
      {
        label: "Submitted",
        data: [45, 60, 55, 70],
        backgroundColor: "#4caf50",
      },
      {
        label: "Pending",
        data: [20, 15, 25, 10],
        backgroundColor: "#ff9800",
      },
    ],
  };

  const columns = [
    { field: "title", headerName: "Assignment", flex: 1, minWidth: 200 },
    { field: "subject", headerName: "Subject", width: 120 },
    { field: "totalStudents", headerName: "Students", width: 110 },
    { field: "submitted", headerName: "Submitted", width: 110 },
    { field: "pending", headerName: "Pending", width: 110 },
    {
      field: "submissionRate",
      headerName: "Submission Progress",
      width: 200,
      renderCell: (params) => {
        const rate = Number(params.row.submissionRate || 0);
        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <LinearProgress
              variant="determinate"
              value={rate}
              sx={{
                flexGrow: 1,
                height: 8,
                borderRadius: 4,
                bgcolor: "grey.200",
              }}
            />
            <Typography variant="caption">
              {params.row.submitted}/{params.row.totalStudents}
            </Typography>
          </Box>
        );
      },
    },
    { field: "dueDate", headerName: "Due Date", width: 120 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={
            params.value === "completed"
              ? "success"
              : params.value === "active"
              ? "primary"
              : "error"
          }
        />
      ),
    },
  ];

  const filteredAssignments = assignments.filter(
    (a) => !statusFilter || a.status === statusFilter
  );

  // Get page title based on context
  const getPageTitle = () => {
    if (canViewGlobalData) return "Assignments Analytics";
    if (isInInstituteContext) return `Assignments - ${selectedInstitute?.name}`;
    return `Assignments - ${userInstitute}`;
  };

  const getPageSubtitle = () => {
    if (canViewGlobalData) return "View all assignments across institutes";
    if (isInInstituteContext)
      return `Assignments overview for ${selectedInstitute?.name}`;
    return `Assignments overview for your institute`;
  };

  const stats = [
    {
      label: "Total Assignments",
      value: String(summary.totalAssignments || 0),
      color: "primary.main",
    },
    {
      label: "Active",
      value: String(summary.active || 0),
      color: "info.main",
    },
    {
      label: "Completed",
      value: String(summary.completed || 0),
      color: "success.main",
    },
    {
      label: "Overdue",
      value: String(summary.overdue || 0),
      color: "error.main",
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {getPageTitle()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getPageSubtitle()}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Download />}>
          Export Report
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <FilterList color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {canViewGlobalData && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Institute</InputLabel>
                  <Select
                    value={instituteFilter}
                    label="Institute"
                    onChange={(e) => setInstituteFilter(e.target.value)}
                  >
                    <MenuItem value="">All Institutes</MenuItem>
                    {institutes.map((inst) => (
                      <MenuItem key={inst.id} value={String(inst.id)}>
                        {inst.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Department"
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={String(dept.id)}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                label="From"
                format={DATE_DISPLAY_FORMAT}
                value={fromDate ? new Date(fromDate) : null}
                onChange={(val) => setFromDate(toApiDate(val))}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                label="To"
                format={DATE_DISPLAY_FORMAT}
                value={toDate ? new Date(toDate) : null}
                onChange={(val) => setToDate(toApiDate(val))}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                }}
                disabled={!fromDate && !toDate}
                sx={{ height: 40 }}
              >
                Clear Date Range
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Submission Status
              </Typography>
              <Box
                sx={{ height: 250, display: "flex", justifyContent: "center" }}
              >
                <Doughnut
                  data={statusData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: { legend: { position: "bottom" } },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Weekly Submission Trend
              </Typography>
              <Box sx={{ height: 250 }}>
                <Bar
                  data={weeklyData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: { legend: { position: "top" } },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Assignments Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {canViewGlobalData ? "All Assignments" : "Assignments"}
          </Typography>
          {period?.startDate && period?.endDate && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Period: {period.startDate} to {period.endDate}
            </Typography>
          )}
          <DataGrid
            rows={filteredAssignments}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
            sx={{ border: "none" }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default AssignmentsAnalytics;
